export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '00:00:00';
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;
  return [hours, minutes, remainingSeconds].map((value) => value.toString().padStart(2, '0')).join(':');
}

/** Duración legible tipo "1h 30m 45s" (omite las unidades en cero de más a la izquierda). */
export function formatDuracionCorta(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0s';
  const totalSeconds = Math.floor(seconds);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const remainingSeconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${remainingSeconds}s`;
  if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
  return `${remainingSeconds}s`;
}

/**
 * Convierte horas decimales (ej. 72.64976944444444, resultado de sumar horas de varias
 * asesorías) a un string legible tipo "72h 38m 59s". Redondea a segundos enteros ANTES de
 * formatear: los acumulados de horas decimales suelen arrastrar ruido de coma flotante
 * (ej. restar 320 - 320 puede dar 3.552713678800501e-15 en vez de 0), y sin este redondeo
 * ese ruido se mostraría tal cual en vez de "0s".
 */
export function formatHorasDecimales(horasDecimales: number): string {
  if (!Number.isFinite(horasDecimales) || horasDecimales < 0) return '0s';
  const totalSegundos = Math.round(horasDecimales * 3600);
  return formatDuracionCorta(totalSegundos);
}

/**
 * Suma la duración de una lista de audios y la devuelve en horas decimales. Usado como estimado
 * en vivo mientras una asesoría está EN_CURSO (su campo `horas` real sigue en 0 hasta que el
 * asesor la envía a revisión). El nombre del campo de duración varía según el endpoint
 * (`duracionSeg` en la mayoría, `duracion` en la vista de detalle de asesoría) — se revisan
 * ambos por eso.
 */
export function horasDeAudios(audios: Array<{ duracionSeg?: number; duracion?: number; estado?: string }> | undefined | null): number {
  // Excluir ELIMINADO: audios ocultos por revisarAudios (rechazo en asesoría reasignada) —
  // siguen en BD pero ya no cuentan como trabajo real.
  return (audios ?? []).filter((a) => a.estado !== 'ELIMINADO').reduce((s, a) => s + (a.duracionSeg ?? a.duracion ?? 0), 0) / 3600;
}

/**
 * Igual que `horasDeAudios`, pero solo cuenta los audios ya aprobados individualmente
 * (`estado: 'APROBADO'`). Sirve para mostrar cuánto de una asesoría RECHAZADA/con correcciones
 * ya fue revisado y aprobado a nivel de audio, aunque la asesoría en general siga pendiente de
 * corregir por otro motivo (ej. una evidencia distinta rechazada).
 */
export function horasDeAudiosAprobados(
  audios: Array<{ duracionSeg?: number; duracion?: number; estado?: string }> | undefined | null
): number {
  // ACTIVO no cuenta: es el estado por defecto de un audio recién subido y sin revisar.
  return horasDeAudios((audios ?? []).filter((a) => a.estado === 'APROBADO'));
}
